// Label line of sight (LABELS-LOS, 2026-10-12): is a label's anchor visible
// from the eye? ONE rule for every label kind (summits, far summits, passes,
// places, huts, POIs, stations, lakes, rivers), on the aim ray's ground
// (aimray.js aimMarch: THE skin where its GROUND-M L0 node is in the client's
// one ground (client/src/ground.js), the node's index box [lower, upper] only
// to find where a node is needed, the far chain outside the frame). Pure (no
// three, no DOM): scripts/check-labels.mjs drives it in Node on the live
// bake's GROUND-M nodes. (rearch-r8k: the lines' "tiles" below are those L0
// nodes — AimGround's keys, the same lattice as the old terrain/L0 tiles.)
//
// The rule (losVerdict). March eye → the label's TARGET (the anchor lifted to
// LOS_CLEAR_M over its ground) and stop at DEPTH_SEEN · d (opts.maxDist):
//   hidden    the ray meets the ground before that — from ANY source: an
//             envelope hit is no later than the true crossing (the envelope
//             is never above the ground), an exact hit is the truth;
//   visible   no hit and the march is FINAL (every stretch decided);
//   undecided no hit, but the line ran under a node's upper where the node
//             is not in: keep the PREVIOUS verdict, and
//             once the label has stayed undecided for LOS_DWELL_MS ask
//             AimGround (lane 'labels') for the tiles. A label never decided
//             is not shown: a label claims its object is in sight.
// DEPTH_SEEN is OPTIC-3's rendered-depth rule: the last 3 % of the line is
// the object's own ground (a hut's slope, a summit's shoulder).
//
// Cost: a march is ~0.02–2 ms (0.5 m strides over an exact node the line runs
// under the top of, a node's exit over the rest). Verdicts are
// cached per POI and re-marched only when due (no verdict; the eye moved
// past moveTol; the target moved; undecided and new tiles landed or
// LOS_RETRY_MS passed). The marches of one inView call draw on a token
// bucket: LOS_RATE ms of march per ms of wall time (2.5 % of a core), at most
// LOS_BURST_MS banked; a march runs only while the bucket holds its
// expected cost (the label's last march, else the last march of any label;
// capped at the burst so a costly one still runs from a full bucket) and its
// measured cost is paid after. The last march of a call used to start on any
// positive balance and overdraw it by up to one march (3 ms burst + a 2 ms
// march = a 5 ms label refresh, in the frame's `lifts` section); now a call
// spends ≤ the burst unless one march alone costs more (gate check-lifts.mjs).
// The bucket may still go negative when a march overruns its estimate: the
// next calls march nothing until it refills. inView marches in rank order, so
// the labels that would show are decided first.
//
// LABELSFIX (2026-10-12): a long line along a valley floor needs 20–40 L0
// tiles; ~20 such labels at once do not fit the shared 64-tile LRU, so a
// march that needed them ALL resident at once never became final (17 of 17
// truly visible labels never shown from Saratz toward Pontresina, ~27 tile
// fetches/s for as long as the optic stayed there). Now:
//   resume    an undecided label keeps its line (eye + target of its first
//             march) and the proven-clear prefix (aimMarch `clear`); while
//             the eye / target stay within tol it re-marches THAT line from
//             the prefix (opts.from) — the tiles behind it may be evicted.
//             A decided verdict is still kept within tol, as before.
//   progress  a label counts the tile fetches that SETTLED for it (landed or
//             failed: AimGround.status) since its prefix last grew; at
//             LOS_STALL_TILES (one request's worth) without progress it stops
//             asking until the prefix grows or its line moves. Plus a total
//             cap per line (LOS_TILE_CAP, keys sent, checked per key).
//   sticky    the target's ground: the exact ground (the skin) once seen is kept
//             per POI (an evicted anchor tile must not drop the target onto
//             the envelope and fire a "target moved" re-march).
//   window    past the window a cached verdict is returned only while its
//             line holds (eye / target within tol), else null.
//
// LABELS-CHURN (2026-10-20): the window's undecided lines that still ask
// HOLD their tiles (AimGround.hold, lane 'labels'): every own tile a re-march
// from the prefix reads, plus the ones it needs (aimray.js wantOf). A tile
// is evicted only once no live line (and not the aim) holds it, so a line
// never loses a tile it has not passed yet, and a tile left behind by every
// line is the one that goes. Before, the 64-tile LRU evicted by landing
// order: the next line, the aim and every renewed line (the eye moved past
// tol) fetched the same stem tiles again (one tile 7× in a short session).

// the rendered-depth rule (OPTIC-3): a surface at >= DEPTH_SEEN · d is the
// object itself or beyond it
export const DEPTH_SEEN = 0.97;
// A label's target stands this far over its ground (max(anchor y, ground)).
// The label names a thing that stands ON the ground — a house, a hut, a
// station, a summit cross, a lake's far shore — and the eye sees the thing,
// not the soil under it: a target AT the soil is a grazing ray that the 0.5 m
// bumps of its own meadow (or a 1.65 m eye on a flat valley floor) occlude.
// 6 m = a two-storey roof / a hut's ridge / a station building; it lifts the
// line by ≤ 6 m · (s / d) along the way, so a crest in front still occludes
// unless it is within 6 m of the eye→anchor line at the anchor (gate
// check-labels ridge row: Celerina behind the San Gian knoll stays hidden). Also absorbs the bake's anchor heights: places carry an
// OSM ele up to 10.7 m UNDER the L0 ground (Celerina), hence the max().
export const LOS_CLEAR_M = 6;
// a decided verdict is re-marched once the eye moved this far from where it
// was marched: min(LOS_MOVE_M, max(1, LOS_MOVE_FRAC · d)) — a near label's
// sight line swings faster
export const LOS_MOVE_M = 4;
export const LOS_MOVE_FRAC = 0.02;
// an undecided verdict re-marches when new tiles landed (aim.gen) or after this
export const LOS_RETRY_MS = 250;
// the dwell rule (main.js AIM_DWELL_MS): a label must stay undecided this
// long, as a candidate, before its tiles are fetched — panning fetches nothing
export const LOS_DWELL_MS = 300;
// a label gone from the candidates this long restarts its dwell
export const LOS_GAP_MS = 400;
// nodes asked per request (the aim asks 8, AIM_REQ_TILES), at most one
// request per LOS_REQ_MS: ≤ 32 nodes/s while labels are undecided
export const LOS_REQ_TILES = 8;
export const LOS_REQ_MS = 250;
// only the first LOS_WINDOW labels of the rank that are not known hidden are
// marched and fetched for: at most LABEL_CAP (12) show, and a hidden label
// ahead of them frees a slot for the next
export const LOS_WINDOW = 24;
// a label stops asking once this many tile fetches settled for it without its
// proven-clear prefix growing (one request's worth), until it grows or the
// line moves; and once it sent this many keys for one line in all (a 16 km
// frame diagonal crosses ≤ ~256 tiles) — it keeps its previous verdict
export const LOS_STALL_TILES = LOS_REQ_TILES;
export const LOS_TILE_CAP = 256;
// the token bucket (ms of march per ms of wall time, ms banked)
export const LOS_RATE = 0.025;
export const LOS_BURST_MS = 3;

// The desktop / phone view's two timed refreshes (main.js _viewPose): the
// pre-band labels every labelMs, the centre range every rangeMs. They used to
// land in ONE frame every lcm (600 ms: the labels' marches + the range march
// together). The range now waits one frame when the labels refresh in its
// frame; the labels never wait. → bit 1 = labels due, bit 2 = range due.
// stagger false = the gates' red twin.
export const VIEW_LABELS = 1, VIEW_RANGE = 2;
export function viewDue(now, labelAt, rangeAt, labelMs, rangeMs, stagger = true) {
  const labels = now - labelAt > labelMs;
  const range = now - rangeAt > rangeMs && !(stagger && labels);
  return (labels ? VIEW_LABELS : 0) | (range ? VIEW_RANGE : 0);
}

// the march result → true (visible) | false (hidden) | null (undecided)
export function losVerdict(r) {
  if (!r) return null;
  if (r.dist !== null) return false;
  return r.final ? true : null;
}

import { wantOf } from './aimray.js';

export class LabelSight {
  // march(O, D, opts)  aimMarch over the client's chain (makeAimGround)
  // ground(x, z)       the anchor's ground: exact where resident, else the
  //                    envelope / far chain (≤ the ground) | null
  // exact(x, z)        the exact ground (the skin) only | null (makeAimGround
  //                    march.exact): kept per POI once seen (sticky)
  // aim                AimGround (gen + status + request(keys, now, 'labels')) | null
  // clock()            ms (performance.now in the client)
  // resume / stall     gates' red twins only: resume false = every march
  //                    from the eye (pre-LABELSFIX), stall Infinity = no
  //                    progress guard, overdraw true = a march starts on any
  //                    positive balance (pre-check-lifts)
  constructor({ march, ground = null, exact = null, aim = null, clock = () => performance.now(),
    rate = LOS_RATE, burst = LOS_BURST_MS, dwell = LOS_DWELL_MS, resume = true, stall = LOS_STALL_TILES,
    overdraw = false } = {}) {
    this.march = march; this.ground = ground; this.exact = exact; this.aim = aim; this.clock = clock;
    this.rate = rate; this.burst = burst; this.dwell = dwell; this.resume = resume; this.stall = stall;
    this.overdraw = overdraw;
    this._lastMs = 0;            // the last march's cost (a fresh label's estimate)
    this.recs = new Map();       // POI → rec (below)
    this._gnd = new Map();       // POI → its exact ground, once seen (sticky)
    this.tokens = burst; this._last = null; this._reqAt = -Infinity;
    this.eye = null; this.now = 0;
    this._reqs = [];
    this._win = [];              // the recs marched / asked for this call (the window)
    // the last call's work: marches, ms spent, tiles asked
    this.stats = { marches: 0, ms: 0, asked: 0 };
  }

  // the target of anchor (x, y, z) of POI p: LOS_CLEAR_M over max(anchor y,
  // ground); the exact ground once seen stays the POI's ground
  target(x, y, z, p = null) {
    let g = p !== null ? this._gnd.get(p) : undefined;
    if (g === undefined) {
      const e = this.exact ? this.exact(x, z) : null;
      if (e !== null && e !== undefined) { g = e; if (p !== null) this._gnd.set(p, e); }
      else g = this.ground ? this.ground(x, z) : null;
    }
    return { x, y: (g !== null && g > y ? g : y) + LOS_CLEAR_M, z };
  }

  // start one inView call from eye {x, y, z}
  begin(eye) {
    const now = this.clock();
    if (this._last !== null) this.tokens = Math.min(this.burst, this.tokens + this.rate * (now - this._last));
    this._last = now;
    this.now = now;
    this.eye = { x: eye.x, y: eye.y, z: eye.z };
    this._reqs = [];
    this._win = [];
    this.stats = { marches: 0, ms: 0, asked: 0 };
  }

  // a march spent outside check() on the same bucket (main.js _viewRange:
  // the centre range's march), paid like a label's march
  charge(ms) {
    if (ms > 0) this.tokens -= ms;
  }

  // does the line (eye e, target t) still stand for eye E / target T?
  static holds(e, t, E, T) {
    const dx = T.x - E.x, dy = T.y - E.y, dz = T.z - E.z;
    const tol = Math.min(LOS_MOVE_M, Math.max(1, LOS_MOVE_FRAC * Math.sqrt(dx * dx + dy * dy + dz * dz)));
    return Math.hypot(E.x - e.x, E.y - e.y, E.z - e.z) <= tol && Math.hypot(T.x - t.x, T.y - t.y, T.z - t.z) <= 1;
  }

  // the verdict for POI p (any stable key) with target T: true | false |
  // null (never decided). Marches when due and the bucket allows; `act`
  // false (past the window): the cached verdict only while its line holds,
  // no march, no fetch.
  check(p, T, A = null, act = true) {
    const now = this.now, E = this.eye;
    let rec = this.recs.get(p);
    if (!rec) {
      if (!act) return null;
      rec = { verdict: null, state: null, at: -Infinity,
        e: { x: 0, y: 0, z: 0 }, t: { x: 0, y: 0, z: 0 }, D: null, d: 0, clear: 0,   // the line, its prefix
        ve: null, vt: null,                                                         // the verdict's line
        gen: -1, need: null, since: now, seen: -Infinity,
        sent: 0, spent: 0, wait: new Set(), ms: null, want: null };
      this.recs.set(p, rec);
    }
    if (!act) return rec.verdict !== null && LabelSight.holds(rec.ve, rec.vt, E, T) ? rec.verdict : null;
    const dx = T.x - E.x, dy = T.y - E.y, dz = T.z - E.z;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    const fresh = rec.at === -Infinity;
    const shifted = !fresh && !LabelSight.holds(rec.e, rec.t, E, T);
    const aim = this.aim;
    const gen = aim ? aim.gen : 0;
    // the fetches asked for this label that settled (landed / failed) count
    // toward the progress guard; dropped ones are simply asked again
    if (aim && aim.status && rec.wait.size) {
      for (const k of rec.wait) {
        const s = aim.status(k, now);
        if (s === 'pending' || s === 'queued') continue;
        rec.wait.delete(k);
        if (s === 'tile' || s === 'failed') rec.spent++;
      }
    }
    const due = fresh || shifted
      || (rec.state === null && (gen !== rec.gen || now - rec.at >= LOS_RETRY_MS));
    // the march's expected cost must fit the balance (see the head comment)
    const est = this.overdraw ? 0 : Math.min(this.burst, rec.ms !== null ? rec.ms : this._lastMs);
    if (due && this.tokens > 0 && this.tokens >= est && d > 1) {
      // a new line (first, or the eye / target moved), else the label's own
      // line again from its proven-clear prefix
      const renew = fresh || shifted || !this.resume;
      if (renew) {
        rec.e = { x: E.x, y: E.y, z: E.z }; rec.t = { x: T.x, y: T.y, z: T.z };
        rec.D = { x: dx / d, y: dy / d, z: dz / d }; rec.d = d;
        if (fresh || shifted) { rec.clear = 0; rec.sent = 0; rec.spent = 0; rec.wait.clear(); }
      }
      const from = this.resume ? rec.clear : 0;
      const t0 = this.clock();
      const r = this.march(rec.e, rec.D, { maxDist: DEPTH_SEEN * rec.d, skip: true, from });
      const ms = this.clock() - t0;
      this.tokens -= ms;
      rec.ms = ms; this._lastMs = ms;
      this.stats.marches++; this.stats.ms += ms;
      const v = losVerdict(r);
      const c = r && Number.isFinite(r.clear) ? r.clear : 0;
      if (v !== null || c > rec.clear + 1e-6) rec.spent = 0;       // progress
      if (this.resume) rec.clear = Math.max(rec.clear, c); else rec.clear = c;
      rec.state = v; rec.at = now; rec.gen = gen;
      if (v !== null) {
        rec.verdict = v; rec.need = null; rec.want = null;
        rec.ve = rec.e; rec.vt = rec.t;
      } else {
        rec.need = r ? r.need : null;
        // what the next march (from the prefix) reads or needs: held
        rec.want = wantOf(r, this.resume ? rec.clear : 0);
      }
    }
    this._win.push(rec);
    // the dwell: undecided, a candidate without a gap → ask for its tiles
    if (rec.state === null && rec.need && rec.need.length) {
      if (now - rec.seen > LOS_GAP_MS) rec.since = now;
      if (now - rec.since >= this.dwell && rec.spent < this.stall && rec.sent < LOS_TILE_CAP) this._reqs.push(rec);
    }
    rec.seen = now;
    return rec.verdict;
  }

  // end the call: one request on the labels' lane, the tiles of the dwelled
  // undecided labels in the order they were checked (inView: rank order),
  // each label's nearest first; a key in flight / waiting is not asked (or
  // counted) again, every label's cap is checked per key. First the hold: the
  // tiles of every undecided line of the window (a stalled one too: it
  // re-marches when one of its tiles lands, and must find the rest)
  end() {
    if (!this.aim) return;
    const aim = this.aim;
    if (aim.hold) {
      const h = [];
      for (const rec of this._win) {
        if (rec.state !== null || !rec.want) continue;
        for (const k of rec.want) h.push(k);
      }
      aim.hold('labels', h, this.now);
    }
    if (this.now - this._reqAt < LOS_REQ_MS) return;
    const keys = [], seen = new Set();
    for (const rec of this._reqs) {
      for (const k of rec.need) {
        if (keys.length >= LOS_REQ_TILES || rec.sent >= LOS_TILE_CAP) break;
        if (seen.has(k) || aim.has(k)) continue;
        if (aim.pending && aim.pending.has(k)) { rec.wait.add(k); continue; }
        // still waiting for room (AimGround._room): asked again, not counted again
        const waiting = rec.wait.has(k) && aim.queueLabels && aim.queueLabels.includes(k);
        seen.add(k); keys.push(k); if (!waiting) rec.sent++; rec.wait.add(k);
      }
      if (keys.length >= LOS_REQ_TILES) break;
    }
    this.stats.asked = keys.length;
    aim.request(keys, this.now, 'labels');
    if (keys.length) this._reqAt = this.now;
  }
}
